// "Ik loop achter" (spec V5): a proposal for the rest of today, applied only after PK confirms (AC3).
import { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { aiError, aiLate, useAiEnabled } from "../lib/ai";
import { latePlan, overrun, shifted, type Anchor } from "@shared/late";
import { displayTime, minutesToTime } from "@shared/time";
import type { DayBlock } from "../lib/db";
import { setBlockStatus, shiftBlocks } from "../lib/days";
import { Sheet } from "./Sheet";
import { Button, cn } from "./ui";

type Choice = { kind: "shift"; minutes: number } | { kind: "drop"; id: string };

const SHIFTS = [
  [15, "Een kwartier later"],
  [30, "Een half uur later"],
  [60, "Een uur later"],
] as const;

/** The button on Vandaag; it only shows when there is something left to move or drop. */
export function LateButton({
  date,
  blocks,
  appointments,
  minute,
}: {
  date: string;
  blocks: DayBlock[];
  appointments: Anchor[];
  minute: number;
}) {
  const [open, setOpen] = useState(false);
  const plan = latePlan(blocks, appointments, minute);
  if (!plan.movable.length) return null;
  return (
    <>
      <Button variant="secondary" className="mb-5 w-full" onClick={() => setOpen(true)}>
        Ik loop achter
      </Button>
      {open && <LateSheet date={date} blocks={blocks} appointments={appointments} minute={minute} onClose={() => setOpen(false)} />}
    </>
  );
}

function LateSheet({
  date,
  blocks,
  appointments,
  minute,
  onClose,
}: {
  date: string;
  blocks: DayBlock[];
  appointments: Anchor[];
  minute: number;
  onClose: () => void;
}) {
  // Fixed when the sheet opens: the proposal does not change under PK's thumb.
  const [plan] = useState(() => latePlan(blocks, appointments, minute));
  const [choice, setChoice] = useState<Choice>({ kind: "shift", minutes: 15 });
  const minutes = choice.kind === "shift" ? choice.minutes : 0;
  const moves = shifted(plan.movable, minutes);
  const clash = choice.kind === "shift" ? overrun(plan.movable, minutes, plan.anchor) : null;
  const dropped = choice.kind === "drop" ? plan.droppable.find((b) => b.id === choice.id) : undefined;

  async function confirm() {
    if (choice.kind === "shift") {
      await shiftBlocks(date, plan.movable, choice.minutes);
      toast(`${plan.movable.length === 1 ? "1 blok" : `${plan.movable.length} blokken`} ${choice.minutes} minuten later`);
    } else if (dropped) {
      await setBlockStatus(dropped, "skipped");
      toast(`${dropped.title} overgeslagen`, {
        duration: 10_000,
        action: { label: "Ongedaan maken", onClick: () => void setBlockStatus(dropped, "open") },
      });
    }
    onClose();
  }

  const option = (key: string, selected: boolean, label: string, onSelect: () => void) => (
    <button
      key={key}
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "flex min-h-12 w-full items-center gap-3 rounded-xl border-2 px-3 text-left",
        selected ? "border-pine bg-paper" : "border-mist bg-paper",
      )}
    >
      <span
        aria-hidden
        className={cn("size-5 shrink-0 rounded-full border-2", selected ? "border-[6px] border-pine" : "border-ink-soft")}
      />
      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{label}</span>
    </button>
  );

  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      title="Ik loop achter"
      description="Kies wat er met de rest van je dag gebeurt. Er verandert pas iets als je bevestigt."
    >
      <LateAdvice
        date={date}
        minute={minute}
        candidates={plan.droppable.map((b) => b.id)}
        onAdvice={(drop, minutes) => {
          if (drop[0]) setChoice({ kind: "drop", id: drop[0] });
          else if (minutes) setChoice({ kind: "shift", minutes });
        }}
      />
      <div role="radiogroup" aria-label="Voorstel" className="mb-4 flex flex-col gap-2">
        {SHIFTS.map(([m, label]) =>
          option(`s${m}`, choice.kind === "shift" && choice.minutes === m, label, () => setChoice({ kind: "shift", minutes: m })),
        )}
        {plan.droppable.map((b) =>
          option(`d${b.id}`, choice.kind === "drop" && choice.id === b.id, `${b.title} overslaan`, () =>
            setChoice({ kind: "drop", id: b.id }),
          ),
        )}
      </div>

      {choice.kind === "shift" && (
        <section aria-label="Nieuwe tijden" className="mb-4 rounded-2xl border border-mist bg-paper p-3">
          <ul className="flex flex-col gap-1">
            {moves.map((s) => (
              <li key={s.block.id} className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{s.block.title}</span>
                <span className="sr-only">
                  van {displayTime(minutesToTime(s.from))} naar {displayTime(minutesToTime(s.to))}
                </span>
                <span aria-hidden className="text-sm text-ink-soft tabular">
                  <s>{displayTime(minutesToTime(s.from))}</s> {displayTime(minutesToTime(s.to))}
                </span>
              </li>
            ))}
          </ul>
          {plan.anchor && <p className="mt-2 text-sm text-ink-soft">{plan.anchor.title} blijft staan, en alles daarna ook.</p>}
          {clash && plan.anchor && (
            <p role="alert" className="mt-2 text-sm font-semibold text-ink">
              {clash.title} loopt dan tegen {plan.anchor.title} aan. Sla liever iets over.
            </p>
          )}
        </section>
      )}
      {dropped && <p className="mb-4 text-ink-soft">{dropped.title} gaat van vandaag af. De rest blijft staan.</p>}

      <Button className="w-full" onClick={confirm}>
        Bevestigen
      </Button>
    </Sheet>
  );
}

/** Met advies (AI golf C): the AI says what is best to drop or how far to move, and why; PK still confirms. */
function LateAdvice({
  date,
  minute,
  candidates,
  onAdvice,
}: {
  date: string;
  minute: number;
  candidates: string[];
  onAdvice: (drop: string[], minutes: number) => void;
}) {
  const ai = useAiEnabled();
  const [advice, setAdvice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!ai) return null;
  if (advice) return <p className="mb-4 rounded-xl border-2 border-sun bg-paper p-3 [overflow-wrap:anywhere]">{advice}</p>;
  return (
    <Button
      variant="quiet"
      className="mb-2"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const answer = await aiLate(date, minutesToTime(minute), candidates);
          setAdvice(answer.advice);
          onAdvice(answer.drop, [15, 30, 60].includes(answer.minutes) ? answer.minutes : 0);
        } catch (error) {
          toast(aiError(error, "Advies"));
        } finally {
          setBusy(false);
        }
      }}
    >
      <Sparkles aria-hidden className="size-5" />
      {busy ? "Bezig met kijken" : "Wat raad je aan?"}
    </Button>
  );
}
